// hooks/useFollowUp.js
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { toast } from "react-toastify";

const headers = () => ({
  Authorization: `Bearer ${localStorage.getItem("cms_token")}`,
  "Content-Type": "application/json",
});

const multipartHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("cms_token")}`,
});

// ── List follow-ups for a lead ───────────────────────────────────────────────
export const useLeadFollowUps = (leadId, filterType = "") => {
  return useQuery({
    queryKey: ["lead-followups", leadId, filterType],
    queryFn: async () => {
      const params = { lead_id: leadId, limit: 50 };
      if (filterType && filterType !== "All") {
        params.type = filterType.toLowerCase();
      }
      const res = await axios.get(`${API_BASE_URL}/lead-followups`, {
        headers: headers(),
        params,
      });

      // console.log("from use-lead-follow-up", res);
      
      return res.data?.data || [];
    },
    enabled: !!leadId,
    keepPreviousData: true,
  });
};

// ── Create follow-up ─────────────────────────────────────────────────────────
export const useAddFollowUp = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { attachments, ...rest } = payload;

      const hasFiles =
        Array.isArray(attachments) && attachments.length > 0 && attachments[0] instanceof File;

      if (hasFiles) {
        const fd = new FormData();
        Object.entries(rest).forEach(([k, v]) => {
          if (v !== "" && v !== null && v !== undefined) {
            fd.append(k, v);
          }
        });
        attachments.forEach((file, idx) => {
          fd.append(`attachments[${idx}]`, file, file.name);
        });

        const res = await axios.post(
          `${API_BASE_URL}/lead-followups`,
          fd,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("cms_token")}`,
            },
          }
        );
        if (res.data?.status === false) throw new Error(res.data.message);
        return { ...res.data, lead_id: rest.lead_id };
      }

      const clean = {};
      Object.entries(rest).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) clean[k] = v;
      });
      const res = await axios.post(
        `${API_BASE_URL}/lead-followups`,
        clean,
        { headers: headers() }
      );
      if (res.data?.status === false) throw new Error(res.data.message);
      return { ...res.data, lead_id: clean.lead_id };
    },
    onSuccess: (data) => {
      toast.success("Follow-up saved successfully");
      const lid = data.lead_id;
      qc.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "lead-followups" &&
          String(query.queryKey[1]) === String(lid),
      });
      qc.invalidateQueries({ queryKey: ["lead-details"] });
      qc.invalidateQueries({ queryKey: ["enquiryList"] });
      qc.invalidateQueries({ queryKey: ["inbox-leads"] });
    },
    onError: (err) => {
      toast.error(err?.message || "Failed to save follow-up");
    },
  });
};

// ── Update follow-up ─────────────────────────────────────────────────────────
// ── Update follow-up ─────────────────────────────────────────────────────────
export const useUpdateFollowUp = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { id, lead_id, attachments, ...rest } = payload;

      const hasFiles =
        Array.isArray(attachments) && attachments.length > 0 && attachments[0] instanceof File;

      if (hasFiles) {
        const fd = new FormData();
        // Include lead_id in the form data
        if (lead_id) fd.append("lead_id", lead_id);
        Object.entries(rest).forEach(([k, v]) => {
          if (v !== "" && v !== null && v !== undefined) {
            fd.append(k, v);
          }
        });
        attachments.forEach((file, idx) => {
          fd.append(`attachments[${idx}]`, file, file.name);
        });

        const res = await axios.put(
          `${API_BASE_URL}/lead-followups/${id}`,
          fd,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("cms_token")}`,
            },
          }
        );
        if (res.data?.status === false) throw new Error(res.data.message);
        return { ...res.data, lead_id };
      }

      // Include lead_id in the JSON body
      const clean = {};
      if (lead_id) clean.lead_id = lead_id;
      Object.entries(rest).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) clean[k] = v;
      });
      const res = await axios.put(
        `${API_BASE_URL}/lead-followups/${id}`,
        clean,
        { headers: headers() }
      );
      if (res.data?.status === false) throw new Error(res.data.message);
      return { ...res.data, lead_id };
    },
    onSuccess: (data) => {
      toast.success("Follow-up updated");
      const lid = data.lead_id;
      qc.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "lead-followups" &&
          String(query.queryKey[1]) === String(lid),
      });
      qc.invalidateQueries({ queryKey: ["enquiryList"] });
      qc.invalidateQueries({ queryKey: ["inbox-leads"] });
    },
    onError: (err) => {
      toast.error(err?.message || "Failed to update follow-up");
    },
  });
};

// ── Delete follow-up ─────────────────────────────────────────────────────────
export const useDeleteFollowUp = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, lead_id }) => {
      const res = await axios.delete(
        `${API_BASE_URL}/lead-followups/${id}`,
        { headers: headers() }
      );
      if (res.data?.status === false) throw new Error(res.data.message);
      return { ...res.data, lead_id };
    },
    onSuccess: (data) => {
      toast.success("Follow-up deleted");
      const lid = data.lead_id;
      qc.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "lead-followups" &&
          String(query.queryKey[1]) === String(lid),
      });
      qc.invalidateQueries({ queryKey: ["enquiryList"] });
      qc.invalidateQueries({ queryKey: ["inbox-leads"] });
    },
    onError: (err) => {
      toast.error(err?.message || "Failed to delete follow-up");
    },
  });
};

// ── Update lead stage status ─────────────────────────────────────────────────
export const useUpdateLeadStage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadId, follow_up_status, remarks }) => {
      const res = await axios.put(
        `${API_BASE_URL}/leads/${leadId}/stage`,
        { follow_up_status, remarks },
        { headers: headers() }
      );
      if (res.data?.status === false) throw new Error(res.data.message);
      return res.data;
    },
    onSuccess: () => {
      toast.success("Lead stage updated");
      qc.invalidateQueries({ queryKey: ["enquiryList"] });
      qc.invalidateQueries({ queryKey: ["inbox-leads"] });
    },
    onError: (err) => {
      toast.error(err?.message || "Failed to update stage");
    },
  });
};